import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Image,
  Spinner,
  Select,
  useToast,
} from "@chakra-ui/react";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";
import SendEmail from "../../components/SendEmail";

const SettlementSection = ({ contractors, contractorsInfo = [], onClose }) => {
  const { user } = useAuth();
  const toast = useToast();
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7).replace("-", "")
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [musicList, setMusicList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [payoutMonth, setPayoutMonth] = useState(0);
  const [payoutTotal, setPayoutTotal] = useState(0);
  const [isRequesting, setIsRequesting] = useState(false);
  const itemsPerPage = 15;

  // 최근 24개월 옵션
  const getMonthOptions = () => {
    const options = [];
    const today = new Date();
    for (let i = 0; i < 24; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const value = `${year}${month}`;
      options.push({ value, label: value });
    }
    return options;
  };

  // 해당 월 마지막 날짜 구하기
  const getLastDateOfMonth = (yyyymm) => {
    const year = Number(yyyymm.slice(0, 4));
    const month = Number(yyyymm.slice(4, 6));
    return new Date(year, month, 0); // 0일은 전달의 마지막 날
  };

  // Ownership 데이터 fetch (RPC로 여러 명 합산)
  useEffect(() => {
    if (!contractors || contractors.length === 0) return;
    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        let allResults = [];
        for (const email of contractors) {
          const { data, error } = await supabase.rpc(
            "get_user_ownership_settlements_paged",
            {
              target_email: email,
              target_month: selectedMonth,
            }
          );
          if (error) throw error;
          if (data) {
            const contractorObj = contractorsInfo.find(
              (c) => c.contractor === email
            );
            const name = contractorObj ? contractorObj.contractor_name : "";
            allResults.push(
              ...data.map((row) => ({ ...row, contractor_name: name }))
            );
          }
        }
        setMusicList(allResults);
        setTotalPages(1); // 여러 명 합산이므로 페이지네이션은 추후 확장
      } catch (err) {
        setError(err.message);
        setMusicList([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [contractors, contractorsInfo, selectedMonth, currentPage]);

  // payout_partner 데이터 fetch
  useEffect(() => {
    if (!user?.email) return;
    const fetchPayout = async () => {
      // 2번: 이번달 정산 금액
      const lastDate = getLastDateOfMonth(selectedMonth);
      const profitDate = `${selectedMonth}${String(lastDate.getDate()).padStart(
        2,
        "0"
      )}`;
      const profitDateISO = new Date(
        `${selectedMonth.slice(0, 4)}-${selectedMonth.slice(4, 6)}-${String(
          lastDate.getDate()
        ).padStart(2, "0")}T00:00:00.000Z`
      );
      // 2번: 이번달 정산 금액
      const { data: monthRows } = await supabase
        .from("payout_partner")
        .select("amount")
        .eq("partner_user", user?.email)
        .eq("profit_date", profitDateISO.toISOString());
      setPayoutMonth(
        monthRows && monthRows.length > 0 ? Number(monthRows[0].amount) : 0
      );
      // 4번: 누적 정산 금액
      const { data: totalRows } = await supabase
        .from("payout_partner")
        .select("amount")
        .eq("partner_user", user?.email);
      setPayoutTotal(
        totalRows && totalRows.length > 0
          ? totalRows.reduce((acc, cur) => acc + Number(cur.amount), 0)
          : 0
      );
    };
    fetchPayout();
  }, [user?.email, selectedMonth]);

  // 1번: 월 수익 파트너(영업 수익 합계)
  const calcBusinessProfit = (list) => {
    return list.reduce((acc, cur) => {
      const commission = Number(cur.commission || 0);
      const total = Number(cur.total_settlement || 0);
      if (commission >= 100) return acc;
      const value =
        (total / ((100 - commission) / 100)) * (commission / 100) * 0.3;
      return acc + Math.round(value * 10) / 10;
    }, 0);
  };
  const businessProfit = calcBusinessProfit(musicList);
  // 3번: 월 정산 가능 잔여 금액
  const remainProfit = businessProfit - payoutMonth;
  const roundedRemainProfit = Math.round(remainProfit);
  const canRequestPayout = roundedRemainProfit > 0;

  // 정산 신청 버튼 클릭
  const handleRequestPayout = async () => {
    if (!user?.email || roundedRemainProfit <= 0) return;
    setIsRequesting(true);
    try {
      const lastDate = getLastDateOfMonth(selectedMonth);
      const profitDateISO = new Date(
        `${selectedMonth.slice(0, 4)}-${selectedMonth.slice(4, 6)}-${String(
          lastDate.getDate()
        ).padStart(2, "0")}T00:00:00.000Z`
      );
      const roundedAmount = Math.round(roundedRemainProfit);
      const { error } = await supabase.from("payout_partner").insert([
        {
          partner_user: user.email,
          amount: roundedAmount,
          profit_date: profitDateISO.toISOString(),
        },
      ]);
      if (error) throw error;
      await SendEmail({
        email: "contact@plpl.kr",
        ccEmails: user?.email ? [user.email] : [],
        subject: `[접수] 파트너스 정산 신청 / ${user?.email}`,
        message: `<p>파트너 이메일: ${user.email}</p>
        <p>신청 월: ${selectedMonth}</p>
        <p>신청 금액: ${roundedRemainProfit.toLocaleString()}원</p>`,
      });
      toast({
        title: "정산 신청이 완료되었습니다.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } catch (e) {
      toast({
        title: "정산 신청에 실패했습니다.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsRequesting(false);
    }
  };

  return (
    <Box bg="white" borderRadius="xl" boxShadow="md" p={6} mt={4} mb={4}>
      {/* 데이터 로딩 시 스피너 */}
      {loading ? (
        <Flex minH="300px" align="center" justify="center">
          <Spinner size="xl" color="teal.400" />
        </Flex>
      ) : (
        <>
          <Flex justify="space-between" align="center" mb={4}>
            <Flex align="center" gap={4}>
              <Select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                w="150px"
                bg="white"
              >
                {getMonthOptions().map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <Text fontWeight="bold">정산 내역</Text>
            </Flex>
            <Button colorScheme="teal" onClick={onClose}>
              닫기
            </Button>
          </Flex>
          {/* 상단 요약 카드 + 정산신청 버튼 */}
          <Flex justify="space-between" align="center" mb={6}>
            <Flex gap={4}>
              <Box bg="gray.50" px={6} py={4} borderRadius="xl" minW="180px">
                <Text fontSize="sm" color="gray.600">
                  [월 수익] 파트너
                </Text>
                <Text fontWeight="bold" fontSize="xl">
                  {Math.round(businessProfit).toLocaleString()}원
                </Text>
              </Box>
              <Box bg="gray.50" px={6} py={4} borderRadius="xl" minW="180px">
                <Text fontSize="sm" color="gray.600">
                  [월 정산 금액] 파트너
                </Text>
                <Text fontWeight="bold" fontSize="xl">
                  {Math.round(payoutMonth).toLocaleString()}원
                </Text>
              </Box>
              <Box bg="gray.50" px={6} py={4} borderRadius="xl" minW="180px">
                <Text fontSize="sm" color="gray.600">
                  [월 정산 가능 잔여 금액] 파트너
                </Text>
                <Text fontWeight="bold" fontSize="xl">
                  {roundedRemainProfit.toLocaleString()}원
                </Text>
              </Box>
              <Box bg="gray.50" px={6} py={4} borderRadius="xl" minW="180px">
                <Text fontSize="sm" color="gray.600">
                  [총 누적 정산] 파트너
                </Text>
                <Text fontWeight="bold" fontSize="xl">
                  {Math.round(payoutTotal).toLocaleString()}원
                </Text>
              </Box>
            </Flex>
            <Button
              colorScheme="blue"
              size="lg"
              onClick={handleRequestPayout}
              isDisabled={!canRequestPayout || isRequesting}
            >
              정산 신청
            </Button>
          </Flex>
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>음반</Th>
                  <Th>영업 수익</Th>
                  <Th>합계</Th>
                  <Th>유튜브/뮤직/쇼츠</Th>
                  <Th>기타 해외</Th>
                  <Th>멜론</Th>
                  <Th>지니</Th>
                  <Th>바이브</Th>
                  <Th>플로</Th>
                </Tr>
              </Thead>
              <Tbody>
                {error ? (
                  <Tr>
                    <Td colSpan={9}>
                      <Text color="red.500" textAlign="center" py={4}>
                        {error}
                      </Text>
                    </Td>
                  </Tr>
                ) : musicList.length === 0 ? (
                  <Tr>
                    <Td colSpan={9}>
                      <Text textAlign="center" py={4} color="gray.500">
                        데이터가 없습니다.
                      </Text>
                    </Td>
                  </Tr>
                ) : (
                  musicList.map((music, idx) => (
                    <Tr key={idx}>
                      <Td>
                        <Flex align="center" gap={3}>
                          <Box
                            w="40px"
                            h="40px"
                            borderRadius="md"
                            overflow="hidden"
                            bg="gray.200"
                          >
                            <Image
                              src={music.cover || music.cover_url}
                              alt={music.name_track}
                              w="100%"
                              h="100%"
                              objectFit="cover"
                            />
                          </Box>
                          <Box>
                            <Text fontWeight="medium">{music.name_track}</Text>
                            <Text fontSize="sm" color="gray.500">
                              {music.name_musician} ({music.contractor_name},{" "}
                              {music.commission}%)
                            </Text>
                          </Box>
                        </Flex>
                      </Td>
                      <Td isNumeric>
                        {/* 영업 수익: 합계/(100-수수료)*3, 소수점 1자리 반올림 후 정수 */}
                        {(() => {
                          const commission = Number(music.commission || 0);
                          const total = Number(music.total_settlement || 0);
                          if (commission >= 100) return "-";
                          const value = (total / (100 - commission)) * 3;
                          return Math.round(value).toLocaleString();
                        })()}
                        원
                      </Td>
                      <Td isNumeric>
                        {Math.round(
                          music.total_settlement || 0
                        ).toLocaleString()}
                        원
                      </Td>
                      <Td isNumeric>
                        {Math.round(music.youtube_amount || 0).toLocaleString()}
                        원
                      </Td>
                      <Td isNumeric>
                        {Math.round(music.global_amount || 0).toLocaleString()}
                        원
                      </Td>
                      <Td isNumeric>
                        {Math.round(music.melon_amount || 0).toLocaleString()}원
                      </Td>
                      <Td isNumeric>
                        {Math.round(music.genie_amount || 0).toLocaleString()}원
                      </Td>
                      <Td isNumeric>
                        {Math.round(music.vibe_amount || 0).toLocaleString()}원
                      </Td>
                      <Td isNumeric>
                        {Math.round(music.flo_amount || 0).toLocaleString()}원
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        </>
      )}
    </Box>
  );
};

export default SettlementSection;
